// [CP01][DM01][UX01] Schnelle, moderne Such-Komponente für Gemeinden/PLZ
'use client';

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

interface Gemeinde {
  nr: string;
  name: string;
  plz: string;
}

interface Props {
  gemeinden: Gemeinde[];
  onSelect: (gemeindeId: string) => void;
}

// Named export for better tree-shaking and explicit imports
export function GemeindeQuickSearch({ gemeinden, onSelect }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Gemeinde[]>([]);
  const [highlight, setHighlight] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);


  useEffect(() => {
    if (!query) {
      setResults([]);
      return;
    }
    const q = query.toLowerCase();
    const filtered = gemeinden
      .filter(g =>
        g.name.toLowerCase().includes(q) ||
        g.plz.startsWith(q)
      )
      .sort((a, b) => {
        // Sort alphabetically by name
        return a.name.localeCompare(b.name);
      })
      .slice(0, 7);
    
    setResults(filtered);
    setHighlight(0);
  }, [query, gemeinden]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      setHighlight(h => Math.min(h + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      setHighlight(h => Math.max(h - 1, 0));
    } else if (e.key === 'Enter' && results[highlight]) {
      onSelect(results[highlight].nr);
      setQuery('');
      setResults([]);
    }
  }

  return (
    <div className="flex flex-col items-center space-y-4 max-w-lg mx-auto">
      {/* Title */}
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white text-center">
        Wählen Sie Ihre Gemeinde
      </h2>
      
      {/* Search Input with Apple-style design */}
      <div className="relative w-full">
        <input
          ref={inputRef}
          type="text"
          className="w-full h-12 px-4 text-base bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-600 rounded-xl shadow-sm transition-all duration-200 focus:outline-none focus:ring-0 focus:border-green-600 dark:focus:border-green-500 focus:shadow-lg placeholder-gray-500 dark:placeholder-gray-400 text-gray-900 dark:text-white"
          placeholder="Gemeinde oder PLZ suchen..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          aria-label="Gemeinde oder PLZ suchen"
          autoFocus
        />
        
        {/* Results Dropdown */}
        <AnimatePresence>
          {results.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -8, scale: 0.98 }}
              transition={{ duration: 0.15, ease: "easeOut" }}
              className="absolute z-50 w-full mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-xl shadow-xl overflow-hidden backdrop-blur-sm"
            >
              <ul className="max-h-64 overflow-auto">
                {results.map((g, i) => (
                  <motion.li
                    key={g.nr}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.12, delay: i * 0.015 }}
                    onMouseDown={() => { onSelect(g.nr); setQuery(''); setResults([]); }}
                    className={`px-4 py-3 cursor-pointer transition-all duration-150 ${
                      i === highlight 
                        ? 'bg-green-50 dark:bg-green-900/20 border-l-4 border-green-600' 
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    } ${i > 0 ? 'border-t border-gray-100 dark:border-gray-700' : ''}`}
                  >
                    <div className="flex items-center">
                      {/* Gemeinde Name and PLZ - More space for readability */}
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 dark:text-white truncate text-lg">{g.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 font-medium">PLZ {g.plz}</div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
